/**
 * Timer — substrate-agnostic delayed-call helper.
 *
 * Phase H10 Arc α (2026-05-08): replaces `scene.time.delayedCall`
 * across the gameplay layer. Thin wrapper over `setTimeout` /
 * `clearTimeout` with a stable handle shape so callers can cancel
 * without storing a raw `setTimeout` id.
 *
 * Notes vs Phaser equivalent:
 *   - `scene.time.delayedCall` paused with the scene; this does NOT
 *     pause. For a 2D MMORPG with continuous server time, that's the
 *     correct behavior — gameplay timers should fire on real time, not
 *     on game-paused time. (Use rAF-driven Tween / AnimEngine for
 *     pause-aware effects instead.)
 *   - The Phaser TimerEvent had additional features (loop, repeat
 *     count, callback args) we never used. Kept narrow on purpose.
 */

export interface TimerHandle {
  /** Cancel the pending timer. No-op if it already fired. */
  cancel(): void;
}

/**
 * Schedule `fn` to run after `ms` milliseconds. Returns a handle for
 * cancellation. Fire-and-forget callers can ignore the return value.
 */
export function delay(ms: number, fn: () => void): TimerHandle {
  const id = setTimeout(fn, ms);
  return {
    cancel(): void {
      clearTimeout(id);
    },
  };
}

/** Cancel a previously-scheduled timer. Safe to call after firing. */
export function cancel(handle: TimerHandle | null | undefined): void {
  handle?.cancel();
}

/**
 * Schedule `fn` to run every `ms` milliseconds (forever, until cancelled).
 * Returns a handle whose `cancel()` stops the recurrence. The first
 * invocation lands after `ms`, not immediately — same as setInterval.
 */
export function interval(ms: number, fn: () => void): TimerHandle {
  const id = setInterval(fn, ms);
  return {
    cancel(): void {
      clearInterval(id);
    },
  };
}
